/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { NOT_APPLICABLE } from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { TSite } from '@global-types/index';
import { useValidImage } from '@hooks/useValidImage';
import { apiClient } from '@lib/api-client';
import { useAuthorization } from '@lib/authorization';
import { QueryConfig } from '@lib/react-query';
import useStore from '@store/index';
import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import {
	formatNumberWithSpaces,
	formatTokenAvailable,
} from '@utils/formats';
import {
	convertGCO2EToTCO2EWithoutUnit,
	formatDate,
} from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import { getUnitLabel } from '@utils/units';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type UseMeasurabilityProjectOptions = {
	projectId: string;
	queryConfig?: QueryConfig<
		typeof getMeasurabilityProjectQueryOptions
	>;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

export const getPublicMeasurabilityProjectById = (
	projectId: string
): Promise<any> => {
	return apiClient.get(
		`/public/measurabilities/${projectId}`,
		{
			params: {
				populate:
					'organization, entity, organization.measurabilities,organization.logo, organization.measurabilities.sites, organization.measurabilities.sites.conversionFactors, organization.measurabilities.sites.meterPoints, organization.entities, organization.entities.projects, organization.entities.projects.sites, organization.sites, organization.sites.conversionFactors, organization.sites.meterPoints, cover, logo, publicSpecs, internalSpecs, supportedBy.logo',
			},
		}
	);
};

export const getPublicMeasurabilityProjectByIdForBrand = (
	projectId: string
): Promise<any> => {
	return apiClient.get(
		`/public/measurabilities/${projectId}`,
		{
			params: {
				populate:
					'organization, entity, organization.measurabilities,organization.logo, organization.entities, organization.entities.projects, cover, logo, publicSpecs, internalSpecs, supportedBy.logo',
			},
		}
	);
};

export const getMeasurabilityProjectById = (
	projectId: string
): Promise<any> => {
	return apiClient.get(`/measurabilities/${projectId}`, {
		params: {
			populate:
				'organization, entity, organization.measurabilities,organization.logo, organization.measurabilities.sites, organization.measurabilities.sites.conversionFactors, organization.measurabilities.sites.meterPoints, organization.entities, organization.entities.projects, organization.entities.projects.sites, organization.sites, organization.sites.conversionFactors, organization.sites.meterPoints, cover, logo, publicSpecs , internalSpecs, supportedBy.logo',
		},
	});
};

export const getMeasurabilityProjectByIdForProducer = (
	projectId: string
): Promise<any> => {
	return apiClient.get(`/measurabilities/${projectId}`, {
		params: {
			populate:
				'organization, entity,organization.logo, organization.entities, organization.sites, organization.sites.conversionFactors, cover, logo, publicSpecs , internalSpecs, supportedBy.logo',
		},
	});
};

export const getMeasurabilityProjectQueryOptions = (
	projectId: string,
	user: any
) => {
	const { checkRole } = useAuthorization();
	return queryOptions({
		queryKey: ['measurability-projects', projectId],
		queryFn: () => {
			if (
				!checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				])
			) {
				if (
					checkRole([
						ROLES.BRAND_ADMIN,
						ROLES.BRAND_STAFF,
					])
				) {
					return getPublicMeasurabilityProjectByIdForBrand(
						projectId
					);
				}
				return getPublicMeasurabilityProjectById(
					projectId
				);
			} else if (
				checkRole([
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
				])
			) {
				return getMeasurabilityProjectByIdForProducer(
					projectId
				);
			}
			return getMeasurabilityProjectById(projectId);
		},
		staleTime: 1000,
	});
};

export const getUserById = (
	userId: string
): Promise<any> => {
	return apiClient.get(`/users/${userId}`);
};

export const getUserQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: ['user-id', userId],
		queryFn: () => getUserById(userId),
	});
};

export const getOrganizationById = (
	organizationId: string
): Promise<any> => {
	return apiClient.get(
		`/organizations/${organizationId}`
	);
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useMeasurabilityProject = ({
	projectId,
	queryConfig,
}: UseMeasurabilityProjectOptions) => {
	const { t } = useTranslation();
	const { getUser } = useStore();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const userData: any = getUser();
	const { checkRole } = useAuthorization();
	const {
		data,
		isFetching,
		isFetched,
		isRefetching,
		isError,
		isLoading,
		isPending,
	} = useQuery({
		...getMeasurabilityProjectQueryOptions(
			projectId,
			userData
		),
		...queryConfig,
	});

	let countryName = '';
	let technolgyProject: any = '';
	let sellPrice = '';
	let cover = '';

	let sites = [];

	if (data && isFetched && !isError) {
		if (data.technology) {
			technolgyProject = getTechnologyProject(
				data.technology
			);
			cover =
				data && data.cover
					? data.cover
					: technolgyProject
						? technolgyProject.image
						: '';
		}

		if (data.location && data.location.countryCode)
			countryName = getCountryName(
				data.location.countryCode
			);

		if (!isNaN(data.sellPrice_europertco2eq))
			sellPrice = `${formatNumberWithSpaces(data.sellPrice_europertco2eq)} ${t('global.sellPriceUnit')}`;

		if (
			data.organization &&
			data.organization.measurabilities &&
			Array.isArray(data.organization.measurabilities) &&
			data.organization.measurabilities.find(
				(item: TSite) => item.id == projectId
			)
		) {
			const foundProject =
				data?.organization?.measurabilities &&
				Array.isArray(data?.organization?.measurabilities)
					? data.organization.measurabilities.find(
							(item: TSite) => item.id == projectId
						)
					: [];

			sites =
				foundProject &&
				foundProject.sites &&
				Array.isArray(foundProject.sites)
					? foundProject.sites.map((_site: TSite) => {
							return {
								id: _site.id,
								name: _site.name,
								unit: _site.unit
									? getUnitLabel(_site.unit.toLowerCase())
									: NOT_APPLICABLE,
								place: _site.location
									? _site.location.place
									: NOT_APPLICABLE,
								countryCode: _site.location
									? _site.location.countryCode
									: NOT_APPLICABLE,
								conversionFactors:
									_site.conversionFactors &&
									Array.isArray(_site.conversionFactors)
										? _site.conversionFactors
												.filter(
													(_conversionFactor: any) =>
														_conversionFactor.startDate !==
														'1970-01-01T00:00:00.000Z'
												)
												.map((_conversionFactor: any) => {
													return {
														id: _conversionFactor.id,
														ratio:
															_conversionFactor.ratio,
														startDate:
															_conversionFactor.startDate
																? formatDate(
																		_conversionFactor.startDate,
																		'DD/MM/YYYY'
																	)
																: NOT_APPLICABLE,
														unit: _conversionFactor.unit
															? getUnitLabel(
																	_conversionFactor.unit.toLowerCase()
																)
															: NOT_APPLICABLE,
													};
												})
										: [],
								meterPoints:
									_site.meterPoints &&
									Array.isArray(_site.meterPoints)
										? _site.meterPoints.map(
												(_meterPoint: any) => {
													return {
														id: _meterPoint.id,
														serialNumber:
															_meterPoint.serialNumber
																? _meterPoint.serialNumber
																: NOT_APPLICABLE,
														countryCode:
															_meterPoint.location
																? _meterPoint.location
																		.countryCode
																: NOT_APPLICABLE,
														place: _meterPoint.location
															? _meterPoint.location.place
															: NOT_APPLICABLE,
														unit: _meterPoint.unit
															? getUnitLabel(
																	_meterPoint.unit.toLowerCase()
																)
															: NOT_APPLICABLE,
													};
												}
											)
										: [],
							};
						})
					: [];
		}
	}

	const { data: user } = useQuery({
		queryKey: [
			'user',
			data &&
				data.organization &&
				data.organization.owner,
		],
		queryFn: () => getUserById(data.organization.owner),
		enabled:
			!checkRole([
				ROLES.PRODUCER_ADMIN,
				ROLES.PRODUCER_ADMIN,
			]) &&
			data &&
			data.organization &&
			data.organization.owner
				? !!data.organization
				: false,
	});

	const { data: contact } = useQuery({
		queryKey: [
			'contact',
			data && data.entity && data.entity.contact,
		],
		queryFn: () => getUserById(data.entity.contact),
		enabled:
			data && data.entity ? !!data.entity.contact : false,
	});

	const coverUrl = useValidImage(cover);
	return {
		data: {
			...data,
			supportedBy: data?.supportedBy?.logo,
			supportedByName:
				data?.supportedBy?.name || NOT_APPLICABLE,
			country: {
				name: countryName,
				code: data?.location?.countryCode.toLowerCase(),
				place: data?.location?.place,
			},
			technology: technolgyProject.label
				? technolgyProject.label
				: NOT_APPLICABLE,
			sellPrice,
			cover: coverUrl,
			availableStock: `${
				data?.quantity?.remaining_aco2
					? formatNumberWithSpaces(
							Number(
								formatTokenAvailable(
									convertGCO2EToTCO2EWithoutUnit(
										data.quantity.remaining_aco2
									)
								)
							)
						)
					: 0
			} ${getUnitLabel('tco2eq')}`,
			remaining_aco2: convertGCO2EToTCO2EWithoutUnit(
				data?.quantity?.remaining_aco2 || 0
			),
			sellPrice_europertco2eq:
				data?.sellPrice_europertco2eq || 0,
		},
		sites,
		user: user ? user : null,
		contactProducer: contact,
		isFetching,
		isFetched,
		isRefetching,
		isError,
		isLoading,
		isPending,
	};
};
